import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { FirebaseError } from '@angular/fire/app';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { TranslocoService } from '@jsverse/transloco';

import { SimpleDialogComponent, SimpleDialogData, SimpleDialogResult } from './simple-dialog/simple-dialog.component';

@Injectable({ providedIn: 'root' })
export class ErrorService {
  private transloco = inject(TranslocoService);
  private matDialog = inject(MatDialog);

  getMessage(error: unknown): string {
    console.error(error);
    if (error instanceof FirebaseError) {
      if (error.code === 'permission-denied') {
        return this.transloco.translate('NO_PERMISSION_TO_ACCESS');
      }
      if (error.code === 'auth/user-not-found') {
        return this.transloco.translate('USER_NOT_FOUND');
      }
      return error.toString();
    }
    if (error instanceof HttpErrorResponse) {
      // the body is plain text instead of json for e.g. unhandled server errors
      if (typeof error.error === 'object' && error.error && 'detail' in error.error) {
        const detail = error.error.detail;
        // an HTTPException sends its message as a string, validation errors are sent as a list
        return typeof detail === 'string' ? detail : detail[0].msg;
      }
    }
    const unknown = this.transloco.translate('UNKNOWN_ERROR_OCCURRED');
    if (error instanceof Error) {
      return `${unknown}
${error.message}`;
    }
    return unknown;
  }

  /**
   * Shows an error dialog for {@link error}.
   * @param retry When given, the dialog offers to retry the failed operation by calling this function.
   */
  handleError(error: unknown, retry?: () => void) {
    const message = this.getMessage(error);
    if (error instanceof HttpErrorResponse && error.status === HttpStatusCode.Unauthorized) {
      // AuthInterceptor will have redirected us to the login page, so we don't need to show an error message
      return;
    }
    this.showErrorDialog(message, retry);
  }

  showErrorDialog(message: string, retry?: () => void): MatDialogRef<SimpleDialogComponent> {
    let ok: string;
    let cancel: string | undefined = undefined;
    if (retry) {
      ok = this.transloco.translate('RETRY');
      cancel = this.transloco.translate('CANCEL');
    } else {
      ok = this.transloco.translate('CLOSE');
    }
    const data: SimpleDialogData = {
      ok,
      cancel,
      message,
      title: this.transloco.translate('ERROR'),
    };
    const dialog = this.matDialog.open<SimpleDialogComponent, SimpleDialogData, SimpleDialogResult>(
      SimpleDialogComponent,
      { data },
    );
    if (retry) {
      dialog.afterClosed().subscribe((result) => {
        if (result?.submitted) {
          retry();
        }
      });
    }
    return dialog;
  }
}
